import React from 'react';
import './BiomarkerRangeScale.scss';
import { 
  GenderRangeData, 
  Biomarker, 
  AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerDTO,
  AppScenariosBiomarkerGetResultsByAppointmentApplicationPayloadBiomarkerDTO
} from '../../../types/apiGeneratedTypes';
import { parseNumericValue } from '../../../utils/biomarkers';

interface BiomarkerRangeScaleProps {
  scale: GenderRangeData;
  currentValue?: number | string;
  biomarker?: Biomarker | AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerDTO | AppScenariosBiomarkerGetResultsByAppointmentApplicationPayloadBiomarkerDTO;
}

export const BiomarkerRangeScale: React.FC<BiomarkerRangeScaleProps> = ({
  scale,
  currentValue,
  biomarker
}) => {
  const isUnitsBiomarker = biomarker?.biomarker_type === 'UNITS';
  const isAntiNuclearBiomarker = biomarker?.name.toLowerCase().includes('anti-nuclear');
  
  if (isUnitsBiomarker || isAntiNuclearBiomarker) {
    return null;
  }

  const segments = [
    {
      key: 'low',
      label: scale?.low_from === null ? 'N/A' : 'Low',
      from: scale?.low_from,
      to: scale?.low_to,
      displayValue: scale?.low_to
        ? `< ${scale?.low_to}`
        : '-',
      background: '#d56256',
      additionalStyle: {
        borderTopLeftRadius: '6px',
        borderBottomLeftRadius: '6px'
      }
    },
    {
      key: 'below-optimal',
      label: scale?.below_optimal_from === null ? 'N/A' : 'Below optimal',
      from: scale?.below_optimal_from,
      to: scale?.below_optimal_to,
      displayValue: scale?.below_optimal_from
        ? `${scale?.below_optimal_from} - ${scale?.below_optimal_to}`
        : '-',
      background:
        '#a3b43e',
      additionalStyle: {}
    },
    {
      key: 'optimal',
      label: scale?.optimal_from === null ? 'N/A' : 'Optimal',
      from: scale?.optimal_from,
      to: scale?.optimal_to,
      displayValue: scale?.optimal_from
        ? `${scale?.optimal_from} - ${scale?.optimal_to}`
        : '-',
      background:
        '#618a3a',
      additionalStyle: {}
    },
    {
      key: 'above-optimal',
      label: scale?.above_optimal_from === null ? 'N/A' : 'Above optimal',
      from: scale?.above_optimal_from,
      to: scale?.above_optimal_to,
      displayValue: scale?.above_optimal_from
        ? `${scale?.above_optimal_from} - ${scale?.above_optimal_to}`
        : '-',
      background:
        '#a3b43e',
      additionalStyle: {}
    },
    {
      key: 'high',
      label: scale?.high_from === null ? 'N/A' : 'High',
      from: scale?.high_from,
      to: scale?.high_to,
      displayValue: scale?.high_from
        ? `≥ ${scale?.high_from}`
        : '-',
      background: '#d56256',
      additionalStyle: {
        borderTopRightRadius: '6px',
        borderBottomRightRadius: '6px'
      }
    }
  ];

  const parsedValue = parseNumericValue(currentValue);

  const getActiveSegmentIndex = () => {
    const parsed = Number(parsedValue);

    if (parsedValue === undefined || isNaN(parsed)) return -1;

    for (let i = 0; i < segments.length; i++) {
      const { from, to, key } = segments[i];

      if (key === 'high') {
        if (typeof from === 'number' && parsed >= from) {
          if (typeof to === 'number') {
            if (parsed <= to) return i;
          } else {
            return i;
          }
        }
        continue;
      }

      if (key === 'below-optimal' && i < segments.length - 1) {
        const optimalSegment = segments.find(s => s.key === 'optimal');
        if (optimalSegment && typeof from === 'number' && typeof optimalSegment.from === 'number') {
          if (parsed >= from && parsed < optimalSegment.from) {
            return i;
          }
        }
        continue;
      }

      if (key === 'above-optimal' && i > 0) {
        const optimalSegment = segments.find(s => s.key === 'optimal');
        const highSegment = segments.find(s => s.key === 'high');
        if (typeof from === 'number') {
          const upperBound = highSegment && typeof highSegment.from === 'number' 
            ? highSegment.from 
            : (typeof to === 'number' ? to : Infinity);
          // Check if value is within the above-optimal range
          if (parsed >= from && parsed <= upperBound) {
            // Also ensure it's above optimal_to if optimal range exists
            if (optimalSegment && typeof optimalSegment.to === 'number') {
              if (parsed > optimalSegment.to) {
                return i;
              }
            } else {
              return i;
            }
          }
        }
        continue;
      }

      if (typeof from === 'number' && typeof to === 'number') {
        if (parsed >= from && parsed <= to) {
          return i;
        }
      }
    }

    return -1;
  };

  const activeIndex = parsedValue !== undefined ? getActiveSegmentIndex() : -1;

  const getIndicatorPosition = (value: number): number => {
    if (isNaN(value)) return 0;

    const firstSegment = segments.find(s => typeof s.from === 'number');
    if (!firstSegment || typeof firstSegment.from !== 'number') return 0;

    if (value < firstSegment.from) return 0;

    const highSegment = segments.find(s => s.key === 'high');
    if (highSegment && typeof highSegment.from === 'number' && value >= highSegment.from) {
      if (typeof highSegment.to === 'number') {
        if (value <= highSegment.to) {
          const fraction = (value - highSegment.from) / (highSegment.to - highSegment.from);
          return 4 * 20 + fraction * 20;
        }
      } else {
        return 100;
      }
    }

    for (let i = 0; i < segments.length; i++) {
      const { from, to, key } = segments[i];

      if (key === 'high') continue;

      if (key === 'below-optimal' && i < segments.length - 1) {
        const optimalSegment = segments.find(s => s.key === 'optimal');
        if (optimalSegment && typeof from === 'number' && typeof optimalSegment.from === 'number') {
          if (value >= from && value < optimalSegment.from) {
            const fraction = (value - from) / (optimalSegment.from - from);
            return i * 20 + fraction * 20;
          }
        }
        continue;
      }

      if (key === 'above-optimal' && i > 0) {
        const highSegment = segments.find(s => s.key === 'high');
        if (typeof from === 'number') {
          const upperBound = highSegment && typeof highSegment.from === 'number' 
            ? highSegment.from 
            : (typeof to === 'number' ? to : Infinity);
          // Check if value is within the above-optimal range
          if (value >= from && value <= upperBound) {
            // Calculate position within the segment
            const segmentWidth = upperBound - from;
            if (segmentWidth > 0) {
              const fraction = (value - from) / segmentWidth;
              return i * 20 + fraction * 20;
            } else {
              return i * 20;
            }
          }
        }
        continue;
      }

      if (typeof from === 'number' && typeof to === 'number') {
        if (value >= from && value <= to) {
          const fraction = (value - from) / (to - from);
          return i * 20 + fraction * 20;
        }
      }
    }

    return 100;
  };

  const scaleStyle: React.CSSProperties =
    parsedValue !== undefined
      ? { position: 'relative' as const, opacity: 1 }
      : {};

  if (!scale || !parsedValue) return null;

  return (
    <div className='values-scale-container'>
      <div className='title'>Preview</div>
      <div className='values-scale' style={scaleStyle}>
        {segments.map((segment, index) => (
          <div
            className='values-scale-item'
            style={segment.additionalStyle}
            key={segment.key}
          >
            <div
              className='values-scale-item-line'
              style={{
                background: segment.background,
                ...segment.additionalStyle
              }}
            />
            <div
              className={`value-container ${index === activeIndex ? 'active' : ''}`}
            >
              <div className='value'>{segment.displayValue}</div>
              <div className='values-scale-item-text'>{segment.label}</div>
            </div>
          </div>
        ))}
        {parsedValue !== undefined && (
          <div
            className='indicator'
            style={{
              position: 'absolute',
              top: '-2px',
              bottom: 0,
              transform: 'translateX(-50%)',
              left: `${getIndicatorPosition(+parsedValue)}%`
            }}
          />
        )}
      </div>
    </div>
  );
};
